import { FunctionComponent, useEffect, useState } from 'react';
import { Button, Card, Checkbox, Col, Collapse, Divider, Flex, Form, FormProps, Input, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

// components
import PageTitle from 'components/title/title';
import BackButton from 'components/backButton/backButton';
import ActionCreateHeaderComponent from 'modules/main/actions/createHeader';
import DeceasedDetailsViewerComponent from '../components/deceasedViewer';
import FileInput from 'components/fileInput/fileInput';
import Icon from 'utils/icon';
import HeirInput, { heirCommonSharesTotal, heirSharesLimit } from '../components/heirInput';

// services
import { onCancelPendingTransactions, onCheckCompanyShareholder, onCreateHeirTransfer } from '../services/heirsService';
import NotificationService from 'core/services/notification';
import { CollapseItemStyles } from 'utils/styles/collapseItem';

// models
import { AvailableShares, CreateHeirTransferPayload, HeirTransfer } from '../models/heir';
import { useDispatch } from 'react-redux';
import { setAvailableShares } from 'stores/useHeirStore';
import { useNavigate } from 'react-router-dom';
import ComponentHeader from 'components/componentHeader/componentHeader';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import { mapResponseErrors } from 'utils/mapResponseErrors';

type FormFields = {
	company_shareholder_id: string;
	heirs: any[];
	heirs_limitation: any;
	distribution_estate: any;
	heirs_error: string; // for "select at least one heir" error display only
};

const SHARE_COUNT_KEYS = [
	'free_common_shares_count',
	'suspended_common_shares_count',
	'mortgaged_common_shares_count',
	'free_preferred_shares_count',
	'suspended_preferred_shares_count',
	'mortgaged_preferred_shares_count',
] as const;

interface HeirsTransactionsCreatePageProps {}

const HeirsTransactionsCreatePage: FunctionComponent<HeirsTransactionsCreatePageProps> = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [createForm] = Form.useForm();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isDeceasedLoading, setIsDeceasedLoading] = useState(false);
	const [cancelModalIsOpen, setCancelModalIsOpen] = useState(false);
	const [isCancelling, setIsCancelling] = useState(false);

	const [deceasedDetails, setDeceasedDetails] = useState<HeirTransfer['deceased']>();
	const [pendingSharesCount, setPendingSharesCount] = useState({ common: 0, preferred: 0 });

	const [limitConfirmValues, setLimitConfirmValues] = useState<FormFields>();
	const [limitApproved, setLimitApproved] = useState(false);

	// remaining = shareholder_max_common_shares minus what the heir already holds
	const heirExceedsShareholderLimit = (heir: any) => {
		const sharesLimit = heirSharesLimit(heir);
		const remaining = sharesLimit?.shareholder_remaining_common_shares;
		return !!sharesLimit?.is_limited && remaining != null && heirCommonSharesTotal(heir) > remaining;
	};

	const closeLimitConfirm = () => {
		setLimitConfirmValues(undefined);
		setLimitApproved(false);
	};

	const checkDeceasedNationalId = async (deceasedNationalId: string) => {
		try {
			setIsDeceasedLoading(true);

			const response = await onCheckCompanyShareholder(deceasedNationalId);

			setDeceasedDetails(response.company_shareholder);

			if (response.has_pending_transactions)
				setPendingSharesCount({
					common: response.pending_common_shares_count,
					preferred: response.pending_preferred_shares_count,
				});

			dispatch(
				setAvailableShares({
					free_common: response.company_shareholder?.free_common_shares_count || 0,
					suspended_common: response.company_shareholder?.suspended_common_shares_count || 0,
					mortgaged_common: response.company_shareholder?.mortgaged_common_shares_count || 0,
					free_preferred: response.company_shareholder?.free_preferred_shares_count || 0,
					suspended_preferred: response.company_shareholder?.suspended_preferred_shares_count || 0,
					mortgaged_preferred: response.company_shareholder?.mortgaged_preferred_shares_count || 0,
				}),
			);

			createForm.setFieldValue('heirs', [{}]);

			setIsDeceasedLoading(false);
		} catch (error: any) {
			const errorMessage = error?.response?.data?.data?.errors?.national_id?.[0] || error?.response?.data.message || t('COMMON.FETCH_ERROR');
			NotificationService.error(t('COMMON.ERROR'), errorMessage);
			setIsDeceasedLoading(false);
		}
	};

	const handleCancelPendingTransactions = async () => {
		try {
			setIsCancelling(true);

			await onCancelPendingTransactions(deceasedDetails?.id!);

			await checkDeceasedNationalId(deceasedDetails?.shareholder.national_id!);

			setPendingSharesCount({ common: 0, preferred: 0 });
			setIsCancelling(false);
			setCancelModalIsOpen(false);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.PENDING_TRANSACTION_CANCELLED'));
		} catch (error) {
			setCancelModalIsOpen(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const computeHeirShareTotals = (heirs: any[] = []) => {
		const totals: any = Object.fromEntries(SHARE_COUNT_KEYS.map((key) => [key, 0]));

		heirs.forEach((heir: any) => {
			if (!heir) return;
			SHARE_COUNT_KEYS.forEach((key) => {
				totals[key] += heir[key] ? Number(heir[key]) : 0;
			});
		});

		return totals;
	};

	const recomputeAvailableShares = (allValues: Record<string, any>) => {
		const totals = computeHeirShareTotals(allValues.heirs);

		dispatch(
			setAvailableShares({
				free_common: deceasedDetails?.free_common_shares_count! - totals.free_common_shares_count,
				suspended_common: deceasedDetails?.suspended_common_shares_count! - totals.suspended_common_shares_count,
				mortgaged_common: deceasedDetails?.mortgaged_common_shares_count! - totals.mortgaged_common_shares_count,
				free_preferred: deceasedDetails?.free_preferred_shares_count! - totals.free_preferred_shares_count,
				suspended_preferred: deceasedDetails?.suspended_preferred_shares_count! - totals.suspended_preferred_shares_count,
				mortgaged_preferred: deceasedDetails?.mortgaged_preferred_shares_count! - totals.mortgaged_preferred_shares_count,
			}),
		);
	};

	const changeHandler: FormProps<FormFields>['onValuesChange'] = (_, allValues) => recomputeAvailableShares(allValues);

	const handleSubmit: FormProps<FormFields>['onFinish'] = (values) => {
		const hasSelectedHeir = (values.heirs || []).some((heir: any) => heir?.shareholder_id);

		if (!hasSelectedHeir) {
			createForm.setFields([
				{
					name: 'heirs_error',
					errors: [t('ACTIONS.SELECT_ONE_AT_LEAST')],
				},
			]);

			return;
		}

		// all of the deceased's shares must be distributed among the heirs
		const totals = computeHeirShareTotals(values.heirs);
		const allSharesDistributed = SHARE_COUNT_KEYS.every((key) => totals[key] === ((deceasedDetails as any)?.[key] || 0));

		if (!allSharesDistributed) {
			createForm.setFields([
				{
					name: 'heirs_error',
					errors: [t('ACTIONS.ALL_SHARES_MUST_BE_DISTRIBUTED')],
				},
			]);

			return;
		}

		createForm.setFields([{ name: 'heirs_error', errors: [] }]);

		if ((values.heirs || []).some(heirExceedsShareholderLimit)) {
			setLimitApproved(false);
			setLimitConfirmValues(values);
			return;
		}

		submitHeirTransfer(values);
	};

	const submitHeirTransfer = async (values: FormFields) => {
		try {
			setIsSubmitting(true);

			const payload = new FormData();
			payload.append('company_shareholder_id', deceasedDetails?.id.toString()!);

			Object.entries(values).forEach(([key, value]) => {
				if (key === 'heirs' || key === 'heirs_error') return;
				if (value === undefined || value === null) return;
				payload.append(key, value);
			});

			(values.heirs || []).forEach((heir: any, heirIndex: number) => {
				if (!heir) return;

				Object.entries(heir).forEach(([key, value]) => {
					if (key === 'name' || key === 'newShareholder' || key === 'shares_limit' || key === 'common_shares_limit') return;
					if (value === undefined || value === null) return;
					payload.append(`heirs[${heirIndex}][${key}]`, value as string);
				});

			});

			await onCreateHeirTransfer(payload);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.HEIR_TRANSFER_CREATED'));

			setIsSubmitting(false);

			navigate('/dashboard/actions/heirs-transfer');
		} catch (error: any) {
			const message = error?.response?.data?.message || t('COMMON.SUBMIT_ERROR');
			closeLimitConfirm();
			mapResponseErrors(error, createForm);
			NotificationService.error(t('COMMON.ERROR'), message);
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			{(!deceasedDetails || pendingSharesCount.common !== 0 || pendingSharesCount.preferred !== 0) && (
				<ActionCreateHeaderComponent
					title={t('ACTIONS.HEIRS_TRANSFER')}
					subTitle={t('ACTIONS.FILL_DETAILS_TO_VALIDATE_ORDER')}
					inputLabel={t('ACTIONS.DECEASED_NATIONAL_ID')}
					isLoading={isDeceasedLoading}
					onSubmit={(nationalId) => checkDeceasedNationalId(nationalId)}
					onChange={() => {
						setPendingSharesCount({ common: 0, preferred: 0 });
						setDeceasedDetails(undefined);
					}}
				/>
			)}

			{(pendingSharesCount.common !== 0 || pendingSharesCount.preferred !== 0) && (
				<>
					<Card className="mt-4 red">
						<ComponentHeader
							title={t('ACTIONS.PENDING_TRANSACTION')}
							subtitle={t('ACTIONS.PENDING_TRANSACTION_DESC')}
							titleStyle={{ color: 'darkred', marginBottom: 0 }}
							subtitleStyle={{ color: 'darkred' }}
						/>
						<Row gutter={[16, 16]}>
							{pendingSharesCount.common > 0 && (
								<Col span={24} md={12}>
									<Typography>{t('ACTIONS.PENDING_COMMON_SHARES')}</Typography>
									<Input value={pendingSharesCount.common} disabled style={{ maxWidth: 'var(--inputWidth)' }} />
								</Col>
							)}

							{pendingSharesCount.preferred > 0 && (
								<Col span={24} md={12}>
									<Typography>{t('ACTIONS.PENDING_PREFERRED_SHARES')}</Typography>
									<Input value={pendingSharesCount.preferred} disabled style={{ maxWidth: 'var(--inputWidth)' }} />
								</Col>
							)}
							<Col span={24}>
								<Flex justify="flex-end">
									<Button type="primary" danger size="middle" onClick={() => setCancelModalIsOpen(true)}>
										{t('ACTIONS.CANCEL_PENDING_TRANSACTIONS')}
									</Button>
								</Flex>
							</Col>
						</Row>
					</Card>
				</>
			)}

			{deceasedDetails && pendingSharesCount.common === 0 && pendingSharesCount.preferred === 0 && (
				<motion.div
					initial={{ opacity: 0, transform: 'translateY(-10px)' }}
					animate={{ opacity: 1, transform: 'translateY(0)' }}
					transition={{ duration: 0.3 }}>
					<Form form={createForm} onFinish={handleSubmit} onValuesChange={changeHandler}>
						<Card className="mb-4">
							<h5 className="mb-3">{t('ACTIONS.DECEASED_EQUITY_INF')}</h5>

							<DeceasedDetailsViewerComponent deceasedDetails={deceasedDetails} />

							<Divider className="mt-0" />

							<div className="position-relative">
								<Card>
									<Form.List name="heirs" initialValue={[{}]}>
										{(fields, { add, remove }) => (
											<>
												<h5 className="mb-3">{t('ACTIONS.NO_OF_HEIRS', { num: fields.length || 1 })}</h5>

												<Collapse
													bordered={false}
													expandIconPosition="end"
													defaultActiveKey={Array.from({ length: 100 }, (_, i) => `${i}`)}
													style={{ background: 'transparent' }}
													collapsible="icon"
													expandIcon={({ isActive }) => (
														<Icon
															name="rightArrow"
															height={18}
															color="#555"
															style={{
																transition: 'all .2s ease-in-out',
																transform: isActive ? 'rotate(-90deg)' : 'rotate(90deg)',
															}}
														/>
													)}
													items={fields.map((field, position) => ({
														key: `${field.key}`,
														label: `${t('ACTIONS.HEIR')} #${position + 1}`,
														children: <HeirInput name={field.name} form={createForm} deceased={deceasedDetails!} />,
														style: { ...CollapseItemStyles, backgroundColor: '#fff' },
														extra: fields.length > 1 && (
															<Button
																type="primary"
																danger
																size="middle"
																onClick={() => {
																	remove(field.name);
																	recomputeAvailableShares(createForm.getFieldsValue());
																}}
																className="fadeIn">
																{t('COMMON.DELETE')}
															</Button>
														),
													}))}
												/>

												<Flex justify="flex-end" className="mt-4">
													<Button type="primary" onClick={() => add({})} size="middle">
														<Icon name="plus" color="#fff" height={14} width={14} />
														{t('ACTIONS.ADD_HEIR', { num: fields.length })}
													</Button>
												</Flex>
											</>
										)}
									</Form.List>
								</Card>
							</div>

							<Divider />

							<Form.Item
								name="heirs_limitation"
								style={{ maxWidth: '100%', marginBottom: 30 }}
								rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
								<FileInput onSelect={(file) => createForm.setFieldValue('heirs_limitation', file)} accept="pdf" name={t('ACTIONS.DETERMINE_HEIRS')} />
							</Form.Item>

							<Form.Item name="distribution_estate" style={{ maxWidth: '100%' }} rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
								<FileInput
									onSelect={(file) => createForm.setFieldValue('distribution_estate', file)}
									accept="pdf"
									name={t('ACTIONS.ESTATE_DISTRIBUTION')}
								/>
							</Form.Item>

							<Flex justify="flex-end" gap={15}>
								<Form.Item name="heirs_error" style={{ marginTop: 15 }}>
									<Input hidden />
								</Form.Item>

								<Button type="primary" htmlType="submit" size="large" className="mt-5" loading={isSubmitting}>
									{t('COMMON.SUBMIT')}
								</Button>
							</Flex>
						</Card>
					</Form>
				</motion.div>
			)}

			<ConfirmModalComponent
				open={cancelModalIsOpen}
				onDiscard={() => setCancelModalIsOpen(false)}
				confirmText={t('ACTIONS.CANCEL_PENDING_TRANSACTIONS')}
				onConfirm={handleCancelPendingTransactions}
				confirmColor="danger"
				title={t('ACTIONS.CANCEL_PENDING_TRANSACTIONS')}
				note={t('ACTIONS.CANCEL_TRANSACTIONS_DECEASED')}
				noteType="red"
				isLoading={isCancelling}
			/>

			<ConfirmModalComponent
				open={!!limitConfirmValues}
				title={t('ACTIONS.SHAREHOLDER_LIMIT_EXCEEDED_TITLE')}
				description={t('ACTIONS.SHAREHOLDER_LIMIT_EXCEEDED_DESCRIPTION')}
				confirmText={t('COMMON.CONFIRM')}
				confirmDisabled={!limitApproved}
				isLoading={isSubmitting}
				onDiscard={closeLimitConfirm}
				onConfirm={() => submitHeirTransfer(limitConfirmValues!)}>
				<Checkbox className="my-3" checked={limitApproved} onChange={(e) => setLimitApproved(e.target.checked)}>
					{t('ACTIONS.SHAREHOLDER_LIMIT_EXCEEDED_APPROVE')}
				</Checkbox>
			</ConfirmModalComponent>
		</>
	);
};

export default HeirsTransactionsCreatePage;
